import { useCallback, useMemo } from 'react';

import { useFastshiftAnalyticsData, useFireAnalyticsEvents } from '@/analytics';
import {
	ActionKey,
	ActionSubjectKey,
	ActionSubjectIdKey,
	SubjectAttributeKey,
} from '@/analytics/constants';
import { isSupportRequestOpen } from '@/common/relationships/src/utils';
import { useFastshiftSelector } from '@/state';

export const useDashboardUIAnalytics = () => {
	const { data: supportRequests } = useFastshiftSelector((state) => state.supportRequests);
	const { sourceKey, identityAttrs } = useFastshiftAnalyticsData(true);
	const { fireUIEvent } = useFireAnalyticsEvents(identityAttrs);

	const hasRequestAssistancePathway = useMemo(
		() => !!supportRequests?.some((req) => isSupportRequestOpen(req.status)),
		[supportRequests],
	);

	// UI event callbacks
	const sendOrgSelectorDropdownClickUIEvent = useCallback(() => {
		fireUIEvent({
			action: ActionKey.CLICKED,
			actionSubject: ActionSubjectKey.DROPDOWN,
			actionSubjectId: ActionSubjectIdKey.ORG_SELECTOR_DROPDOWN,
			source: sourceKey,
			attributes: {
				hasRequestAssistancePathway,
			},
		});
	}, [fireUIEvent, hasRequestAssistancePathway, sourceKey]);

	const sendOrgSelectorDropdownItemClickUIEvent = useCallback(
		(selectedOrgId: string) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.DROPDOWN_ITEM,
				actionSubjectId: ActionSubjectIdKey.ORG_SELECTOR_DROPDOWN,
				source: sourceKey,
				attributes: {
					selectedOrgId,
				},
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendMigrationSummarySectionLinkClickUIEvent = useCallback(
		(subject: SubjectAttributeKey, selectedOrgId: string) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.LINK,
				actionSubjectId: ActionSubjectIdKey.MIGRATION_SUMMARY_SECTION,
				source: sourceKey,
				attributes: {
					subject,
					selectedOrgId,
				},
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendMigrationTicketsTopSectionOpenSupportTicketLinkClickUIEvent = useCallback(
		(ticketId: string) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.LINK,
				actionSubjectId: ActionSubjectIdKey.MIGRATION_TICKETS_TOP_SECTION_OPEN_SUPPORT_TICKET_LINK,
				source: sourceKey,
				attributes: {
					ticketId,
				},
			});
		},
		[fireUIEvent, sourceKey],
	);

	const sendMigrationTicketsListViewAllButtonClickUIEvent = useCallback(() => {
		fireUIEvent({
			action: ActionKey.CLICKED,
			actionSubject: ActionSubjectKey.LINK,
			actionSubjectId: ActionSubjectIdKey.MIGRATION_TICKETS_TOP_SECTION_VIEW_ALL_LINK,
			source: sourceKey,
			attributes: {},
		});
	}, [fireUIEvent, sourceKey]);

	return {
		sendOrgSelectorDropdownClickUIEvent,
		sendOrgSelectorDropdownItemClickUIEvent,
		sendMigrationSummarySectionLinkClickUIEvent,
		sendMigrationTicketsTopSectionOpenSupportTicketLinkClickUIEvent,
		sendMigrationTicketsListViewAllButtonClickUIEvent,
	};
};
